import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { tokens } from '../../global/theme/tokens';

// decision => [label, API decision, API scope]
const CHOICES = [
  ['once', 'Allow once', 'approve', 'once'],
  ['chat', 'Allow in this chat', 'approve', 'chat'],
  ['always', 'Always allow', 'approve', 'always'],
  ['deny', 'Deny', 'reject', null],
];

/**
 * The permissions a request asks for: usually one, sometimes several (a
 * tool that touches two areas). Older responses only carry the first.
 */
const permissionsOf = (approval) =>
  approval.permissions?.length
    ? approval.permissions
    : approval.permissionKey
      ? [
          {
            key: approval.permissionKey,
            level: approval.level,
            label: approval.label,
            group: approval.group,
          },
        ]
      : [];

/**
 * A paused turn: the AI called a tool it has no permission for and waits
 * for the user to allow it (once, for this chat or always) or deny it.
 * With several requests, the decisions are sent once all are made.
 */
export const AiChatApprovalCard = ({ approvals, canDecide, onDecide }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const [decisions, setDecisions] = useState({});
  const [submitting, setSubmitting] = useState(false);
  // The ids the decisions were sent for. A ref, not state: onDecide writes
  // the grant cookie synchronously, which re-renders the panel (and this
  // card with a new onDecide) before the submitting state has landed, so
  // a submit driven by state or an effect would fire again and again.
  const sentRef = useRef(null);

  const ids = approvals.map((a) => a.id).join(',');
  useEffect(() => {
    setDecisions({});
    setSubmitting(false);
    sentRef.current = null;
  }, [ids]);

  const submit = (complete) => {
    if (sentRef.current === ids) return;
    sentRef.current = ids;
    setSubmitting(true);
    onDecide(
      approvals.map((a) => {
        const choice = CHOICES.find(([key]) => key === complete[a.id]);
        return {
          id: a.id,
          decision: choice[2],
          ...(choice[3] ? { scope: choice[3] } : {}),
          permissionKeys: permissionsOf(a).map((p) => p.key),
        };
      }),
    ).catch(() => {
      sentRef.current = null;
      setDecisions({});
      setSubmitting(false);
    });
  };

  // sent as soon as every request has its decision
  const choose = (approvalId, choice) => {
    if (sentRef.current === ids) return;
    const next = { ...decisions, [approvalId]: choice };
    setDecisions(next);
    if (approvals.every((a) => next[a.id])) submit(next);
  };

  const decided = Object.keys(decisions).length;

  return (
    <Box
      className="flex flex-col gap-2 px-3 py-2.5 rounded-xl"
      sx={{
        backgroundColor: `${colors.warning}12`,
        border: `1px solid ${colors.warning}50`,
      }}
    >
      <Box className="flex items-center gap-1.5">
        <ShieldOutlinedIcon sx={{ fontSize: 18, color: colors.warning }} />
        <Typography variant="subtitle2" fontWeight={600} className="flex-1">
          {t('The AI asks for permission')}
        </Typography>
        {submitting && <CircularProgress size={14} />}
      </Box>

      {approvals.map((approval) => (
        <ApprovalRequest
          key={approval.id}
          approval={approval}
          decision={decisions[approval.id]}
          disabled={!canDecide || submitting}
          onChoose={(choice) => choose(approval.id, choice)}
        />
      ))}

      {!canDecide && (
        <Typography variant="caption" sx={{ opacity: 0.6 }}>
          {t(
            'Waiting for a participant with write access to allow or deny this.',
          )}
        </Typography>
      )}
      {canDecide && approvals.length > 1 && (
        <Typography variant="caption" sx={{ opacity: 0.6 }}>
          {t('{{decided}} of {{total}} decided', {
            decided,
            total: approvals.length,
          })}
        </Typography>
      )}
    </Box>
  );
};

const ApprovalRequest = ({ approval, decision, disabled, onChoose }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const args =
    approval.arguments && Object.keys(approval.arguments).length > 0
      ? JSON.stringify(approval.arguments, null, 2)
      : null;
  const permissions = permissionsOf(approval);

  return (
    <Box
      className="flex flex-col gap-1.5 px-2.5 py-2 rounded-lg"
      sx={{ backgroundColor: colors.card, border: `1px solid ${colors.bgInput}` }}
    >
      <Typography variant="caption" sx={{ opacity: 0.6 }}>
        {approval.tool}
      </Typography>
      {permissions.map((permission) => (
        <PermissionLine key={permission.key} permission={permission} />
      ))}
      <Box className="flex items-center gap-1 flex-wrap">
        {args && (
          <Box
            onClick={() => setOpen(!open)}
            className="inline-flex items-center cursor-pointer select-none ml-auto"
            sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}
          >
            <Typography variant="caption">{t('Details')}</Typography>
            <ExpandMoreIcon
              sx={{
                fontSize: 16,
                transform: open ? 'rotate(180deg)' : 'none',
                transition: '0.2s ease',
              }}
            />
          </Box>
        )}
      </Box>
      {args && (
        <Collapse in={open} unmountOnExit>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1,
              maxHeight: 160,
              overflow: 'auto',
              fontSize: '0.7rem',
              fontFamily: 'Source Code Pro, monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              backgroundColor: colors.bgInput,
              borderRadius: '6px',
            }}
          >
            {args}
          </Box>
        </Collapse>
      )}
      <Box className="flex flex-wrap gap-1 pt-0.5">
        {CHOICES.map(([key, label]) => {
          const selected = decision === key;
          const isDeny = key === 'deny';
          // "Always allow" is what most users want: solid green from the start
          const preferred = key === 'always';
          return (
            <Button
              key={key}
              size="small"
              variant={selected || preferred ? 'contained' : 'outlined'}
              color={isDeny ? 'error' : 'success'}
              disabled={disabled || (decision && !selected)}
              onClick={() => onChoose(key)}
              sx={{
                textTransform: 'none',
                fontSize: '0.72rem',
                py: 0.25,
                px: 1.25,
                minWidth: 0,
                borderRadius: '8px',
                ...(isDeny ? { ml: 'auto' } : {}),
              }}
            >
              {t(label)}
            </Button>
          );
        })}
      </Box>
    </Box>
  );
};

const PermissionLine = ({ permission }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const isWrite = permission.level === 'write';

  return (
    <Box className="flex flex-col gap-0.5">
      <Typography variant="body2" fontWeight={500}>
        {permission.label ? t(permission.label) : permission.key}
      </Typography>
      <Box className="flex items-center gap-1 flex-wrap">
        {permission.group && (
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            {t(permission.group.name)}
          </Typography>
        )}
        {permission.level && (
          <Typography
            variant="caption"
            fontWeight={600}
            className="px-1.5 rounded"
            sx={{
              backgroundColor: `${isWrite ? colors.warning : colors.info}25`,
              color: isWrite ? colors.warning : colors.info,
              fontSize: '0.65rem',
              textTransform: 'uppercase',
            }}
          >
            {t(isWrite ? 'Write' : 'Read')}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
